import { useEffect, useRef, useState } from 'react';

function WhyUs() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      icon: 'fa-users',
      title: 'Expertise',
      description:
        'Notre équipe réunit des experts en finance, RH et formation avec plus de 15 ans d’expérience dans des secteurs variés.',
    },
    {
      icon: 'fa-puzzle-piece',
      title: 'Customisation',
      description:
        'Nous adaptons nos solutions aux besoins spécifiques de chaque client, en concevant des programmes sur mesure et des stratégies personnalisées.',
    },
    {
      icon: 'fa-headset',
      title: 'Support dédié',
      description:
        'Chaque client bénéficie d’un responsable de compte attitré, disponible pour un accompagnement continu et réactif.',
    },
    {
      icon: 'fa-star',
      title: 'Taux de succès',
      description:
        'Plus de 95% de nos clients recommandent nos services, avec des résultats concrets mesurés par des études de cas et des témoignages.',
    },
  ];

  const testimonials = [
    {
      name: 'Marie Dupont',
      role: 'Directrice RH, Groupe Ouest',
      text: '3iSENTER nous a accompagnés dans la refonte de notre politique de formation. Leur approche sur mesure a boosté l’engagement de nos équipes.',
      avatar: 'M',
    },
    {
      name: 'Thomas Lefèvre',
      role: 'CEO, StartUp Innov',
      text: 'Grâce à leur recrutement ciblé, nous avons trouvé en moins de 2 semaines le profil idéal pour notre département R&D.',
      avatar: 'T',
    },
  ];

  return (
    <>
      <style>{`
        /* ============================================
           WHY CHOOSE US – fond bleu solide
           ============================================ */
        .ve-why-section {
          padding: 100px 0 80px;
          background: #0a1628;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #ffffff;
          position: relative;
          overflow: hidden;
           max-width: 100%;
        }

        .ve-why-section::before {
          content: '';
          position: absolute;
          top: -20%;
          right: -10%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(42, 95, 168, 0.08), transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          position: relative;
          z-index: 2;
        }

        /* En-tête */
        .ve-why-header {
          text-align: center;
          margin-bottom: 56px;
          animation: fadeUp 0.8s ease forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        .ve-section-tag {
          display: inline-block;
          background: rgba(74, 144, 226, 0.15);
          color: #4a90e2;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          padding: 6px 20px;
          border-radius: 50px;
          text-transform: uppercase;
          margin-bottom: 16px;
        }

        .ve-why-header h2 {
          font-size: clamp(2rem, 4.5vw, 3.2rem);
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px;
          letter-spacing: -0.5px;
        }

        .ve-why-header h2 span {
          background: linear-gradient(135deg, #4a90e2, #6bb3e8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .ve-why-header p {
          font-size: 1.1rem;
          color: rgba(255,255,255,0.75);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* Grille des piliers */
        .ve-pillars-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 30px;
          margin-bottom: 70px;
        }

        .ve-pillar-card {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(6px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 24px;
          padding: 30px 22px 26px;
          text-align: center;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s, background 0.3s;
          opacity: 0;
          transform: translateY(40px);
          animation: cardFadeUp 0.8s ease forwards;
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
        }

        .ve-pillar-card:nth-child(1) { animation-delay: 0.05s; }
        .ve-pillar-card:nth-child(2) { animation-delay: 0.10s; }
        .ve-pillar-card:nth-child(3) { animation-delay: 0.15s; }
        .ve-pillar-card:nth-child(4) { animation-delay: 0.20s; }

        @keyframes cardFadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        .ve-pillar-card:hover {
          transform: translateY(-8px) scale(1.02);
          background: rgba(255,255,255,0.08);
          box-shadow: 0 12px 40px rgba(0,0,0,0.3);
          border-color: rgba(74, 144, 226, 0.25);
        }

        .ve-pillar-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          border-radius: 18px;
          background: rgba(74, 144, 226, 0.15);
          font-size: 2rem;
          color: #4a90e2;
          margin-bottom: 18px;
          transition: transform 0.4s, background 0.3s;
        }

        .ve-pillar-card:hover .ve-pillar-icon {
          transform: scale(1.08) rotate(-4deg);
          background: rgba(74, 144, 226, 0.25);
        }

        .ve-pillar-card h4 {
          font-size: 1.3rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0 0 10px;
        }

        .ve-pillar-card p {
          font-size: 0.95rem;
          line-height: 1.6;
          color: rgba(255,255,255,0.75);
          margin: 0;
          flex: 1;
        }

        /* Section Témoignages */
        .ve-testimonials {
          margin-top: 20px;
        }

        .ve-testimonials h3 {
          text-align: center;
          font-size: 1.6rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 32px;
        }

        .ve-testimonials h3 span {
          color: #4a90e2;
        }

        .ve-testimonial-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
        }

        .ve-testimonial-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 28px 24px;
          transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.3s;
          opacity: 0;
          transform: translateY(30px);
          animation: cardFadeUp 0.8s ease 0.25s forwards;
        }

        .ve-testimonial-card:hover {
          transform: translateY(-6px);
          background: rgba(255,255,255,0.08);
          border-color: rgba(74, 144, 226, 0.2);
        }

        .ve-testimonial-quote {
          font-size: 1.8rem;
          color: #4a90e2;
          opacity: 0.4;
          margin-bottom: 8px;
          line-height: 1;
        }

        .ve-testimonial-card p {
          font-size: 0.95rem;
          line-height: 1.6;
          color: rgba(255,255,255,0.85);
          margin: 0 0 16px;
          font-style: italic;
        }

        .ve-testimonial-author {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .ve-testimonial-avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b7fd4, #4a90e2);
          font-size: 1.2rem;
          font-weight: 700;
          color: #ffffff;
          flex-shrink: 0;
        }

        .ve-testimonial-author div {
          display: flex;
          flex-direction: column;
        }

        .ve-testimonial-author strong {
          font-size: 1rem;
          color: #ffffff;
        }

        .ve-testimonial-author span {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.6);
        }

        /* Animation */
        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .ve-why-section {
            padding: 70px 0 50px;
          }
          .ve-pillars-grid {
            grid-template-columns: 1fr 1fr;
          }
          .ve-testimonial-grid {
            grid-template-columns: 1fr;
            max-width: 500px;
            margin: 0 auto;
          }
        }

        @media (max-width: 480px) {
          .ve-pillars-grid {
            grid-template-columns: 1fr;
            max-width: 360px;
            margin: 0 auto 40px;
          }
          .ve-testimonial-card {
            padding: 20px 16px;
          }
        }

        /* Fallback FontAwesome */
        .fa, .fas, .far, .fal, .fab {
          font-family: 'Font Awesome 6 Free' !important;
          font-weight: 900;
        }
        .fa-users::before { content: "👥"; font-style: normal; }
        .fa-puzzle-piece::before { content: "🧩"; font-style: normal; }
        .fa-headset::before { content: "🎧"; font-style: normal; }
        .fa-star::before { content: "⭐"; font-style: normal; }
        .fa-quote-left::before { content: "«"; font-style: normal; }
      `}</style>

      <section className="ve-why-section" ref={sectionRef}>
        <div className="container">
          <div className="ve-why-header">
            <span className="ve-section-tag">Pourquoi 3iSENTER ?</span>
            <h2>Des atouts qui font la <span>différence</span></h2>
            <p>
              Découvrez les 4 piliers qui font de 3iSENTER un partenaire de confiance
              pour votre développement professionnel et organisationnel.
            </p>
          </div>

          <div className="ve-pillars-grid">
            {pillars.map((pillar, idx) => (
              <div className="ve-pillar-card" key={idx}>
                <div className="ve-pillar-icon"><i className={`fa ${pillar.icon}`}></i></div>
                <h4>{pillar.title}</h4>
                <p>{pillar.description}</p>
              </div>
            ))}
          </div>

          <div className="ve-testimonials">
            <h3>Ils nous font <span>confiance</span></h3>
            <div className="ve-testimonial-grid">
              {testimonials.map((t, idx) => (
                <div className="ve-testimonial-card" key={idx}>
                  <div className="ve-testimonial-quote"><i className="fa fa-quote-left"></i></div>
                  <p>“{t.text}”</p>
                  <div className="ve-testimonial-author">
                    <div className="ve-testimonial-avatar">{t.avatar}</div>
                    <div>
                      <strong>{t.name}</strong>
                      <span>{t.role}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default WhyUs;